// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { Box, Flex } from '@mantine/core';

import { MailButton } from 'app/Layout/HeaderBar/components/MailButton';
import { HelpDeskPopover } from 'app/Layout/HeaderBar/HelpDeskDropdown/HelpDeskDropdown';
import { ProfileDropdown } from 'app/Layout/HeaderBar/ProfileDropdown/ProfileDropdown';
import { NAV_BAR_FOOTER_HEIGHT, navbarTransitionStyle, useAppShell } from 'app/Layout/NavBar/useAppShell';

export type NavbarFooterItemProps = {
  navbarExpanded: boolean;
  popoverOpened: boolean;
  onPopoverChanged: (popoverOpened: boolean) => void;
};

export const navbarFooterItems = [
  { key: 'help', Component: HelpDeskPopover },
  { key: 'mail', Component: MailButton },
  { key: 'profile', Component: ProfileDropdown },
] as const satisfies { key: string; Component: React.FC<NavbarFooterItemProps> }[];
export type NavbarFooterItemKey = (typeof navbarFooterItems)[number]['key'];

const ITEM_SIZE = 35;

type Props = {
  activePopover: NavbarFooterItemKey | null;
  onPopoverChanged: (key: NavbarFooterItemKey | null) => void;
};

export const NavBarFooter = ({ activePopover, onPopoverChanged }: Props) => {
  const { navbarExpanded, navbarWidth } = useAppShell();

  const spacerSize = navbarExpanded
    ? (navbarWidth - ITEM_SIZE * navbarFooterItems.length) / (navbarFooterItems.length * 2)
    : ITEM_SIZE / 4;

  return (
    <Box
      h={
        navbarExpanded
          ? NAV_BAR_FOOTER_HEIGHT
          : navbarFooterItems.length * ITEM_SIZE + spacerSize * navbarFooterItems.length * 2
      }
      style={{
        transition: navbarTransitionStyle,
      }}
    >
      {navbarFooterItems.map(({ key, Component }, index) => {
        const left = navbarExpanded ? spacerSize + spacerSize * index * 2 + ITEM_SIZE * index : 10;
        const bottom = navbarExpanded
          ? (NAV_BAR_FOOTER_HEIGHT - ITEM_SIZE) / 2 + 6
          : (navbarFooterItems.length - index) * ITEM_SIZE - spacerSize * index;
        return (
          <Flex
            key={key}
            justify="center"
            align="center"
            w={ITEM_SIZE}
            h={ITEM_SIZE}
            style={{
              position: 'absolute',
              left,
              bottom,
              transition: navbarTransitionStyle,
            }}
          >
            <Component
              navbarExpanded={navbarExpanded}
              popoverOpened={activePopover === key}
              onPopoverChanged={(popoverOpened) => onPopoverChanged(popoverOpened ? key : null)}
            />
          </Flex>
        );
      })}
    </Box>
  );
};
